import React, { useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useCountUp } from 'react-countup'
import { Bar, Line } from '@visx/shape'
import { Point } from '@visx/point'
import { Group } from '@visx/group'
import { useTooltip, useTooltipInPortal } from '@visx/tooltip'
import { localPoint } from '@visx/event'
import { scaleBand, scaleLinear } from '@visx/scale'

import { formatToCurrency, formatAbbrCurrency } from '../../helpers'

import styles from './Charts.module.scss'

// arbitrary spacing
const verticalMargin = 20

const BreakEvenBarChart = ({
  width,
  height,
  data,
  outsideScopeIndex, // ie. years in home highlighting
  breakEvenIndex, // ie. vertical dashed gray line
  withEllipsis,
  compact,
}) => {
  const xMax = width
  const yMax = height - verticalMargin

  // scales, memoize for performance
  const xScale = useMemo(
    () =>
      scaleBand({
        range: [0, xMax],
        round: true,
        domain: data.map(d => d.x),
        padding: 0.4,
      }),
    [xMax, data]
  )
  const yScale = useMemo(
    () =>
      scaleLinear({
        range: [yMax, 0],
        round: true,
        domain: [
          Math.min(...data.map(d => d.y)),
          Math.max(...data.map(d => d.y)),
        ],
      }),
    [yMax, data]
  )

  // tooltip methods via hook and mouseOver event to wire in
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip()
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  })
  const handleMouseOver = (event, datum) => {
    const coords = localPoint(event.target.ownerSVGElement, event)
    const left = xScale(datum.x) - xScale.bandwidth() / 1.5 - 35
    showTooltip({
      tooltipLeft: Math.min(xMax - 63, Math.max(-26, left)),
      tooltipTop: coords.y - 65,
      tooltipData: datum,
    })
  }

  // utility
  const getColor = ({ x, y }) => {
    if (x > outsideScopeIndex) {
      return y >= 0 ? styles['color-dark-green'] : styles['color-dark-red']
    } else {
      return y >= 0 ? styles['color-green'] : styles['color-red']
    }
  }

  if (!data || !data.length || !height || !width || width < 10) {
    return <></>
  }

  // calcs for breakeven
  let breakEvenFrom, breakEvenTo
  if (breakEvenIndex > 0 && breakEvenIndex < data.length) {
    const breakX = xScale(data[breakEvenIndex].x) - 3
    breakEvenFrom = new Point({ x: breakX, y: -5 })
    breakEvenTo = new Point({ x: breakX, y: yMax + 5 })
  }

  // calcs for ellipsis
  const ellipsisData = data[data.length - 1]
  const ellipsisX = xScale(ellipsisData.x) + xScale.bandwidth() + 4
  const ellipsisY =
    ellipsisData.y > 0 ? yScale(0) - 1 : yScale(yMax) - yScale(0) + 1

  return (
    <>
      <svg width={width} height={height} ref={containerRef}>
        <Group top={verticalMargin / 2}>
          {data.map((d, i) => (
            <SlideBar
              className={styles.slideBar}
              key={`bar-${i}`}
              datum={d}
              xScale={xScale}
              yScale={yScale}
              getColor={getColor}
              onMouseOver={e => {
                !compact && handleMouseOver(e, d)
              }}
              onMouseOut={hideTooltip}
            />
          ))}
          {breakEvenFrom && !compact && (
            <Line
              key='break-even-line'
              from={breakEvenFrom}
              to={breakEvenTo}
              stroke={styles['color-gray']}
              strokeDasharray='5 4'
              strokeWidth='2'
            />
          )}
          {withEllipsis && !compact && (
            <Ellipsis
              xStart={ellipsisX}
              yStart={ellipsisY}
              fill={getColor(ellipsisData)}
            />
          )}
        </Group>
      </svg>
      {tooltipOpen && (
        <TooltipInPortal
          key={Math.random()}
          top={tooltipTop}
          left={tooltipLeft}
          className={styles.ttPortal}
        >
          <strong>{`Year ${tooltipData.x}`}</strong>
          <span>
            {tooltipData.y >= 100000
              ? formatAbbrCurrency(tooltipData.y)
              : formatToCurrency(tooltipData.y)}
          </span>
        </TooltipInPortal>
      )}
    </>
  )
}

// Three dots on the right of the chart
const Ellipsis = ({ xStart, yStart, fill }) => (
  <>
    <circle key='ellipsis-point-1' cx={xStart} cy={yStart} r={1} fill={fill} />
    <circle
      key='ellipsis-point-2'
      cx={xStart + 4}
      cy={yStart}
      r={1}
      fill={fill}
    />
    <circle
      key='ellipsis-point-3'
      cx={xStart + 8}
      cy={yStart}
      r={1}
      fill={fill}
    />
  </>
)

// Uses react-countup to interploate change in svg position/size
const SlideBar = ({ datum, getColor, xScale, yScale, ...restProps }) => {
  const {
    countUp,
    start: startCount,
    update: updateCount,
  } = useCountUp({
    start: 0,
    end: datum.y,
    duration: 0.1,
    useEasing: false,
  })

  useEffect(() => {
    updateCount(datum.y)
  }, [datum.y]) // eslint-disable-line

  useEffect(() => {
    startCount()
  }, []) // eslint-disable-line

  // the actual important mathy bits for chart svg bars
  const barWidth = xScale.bandwidth()
  const barHeight = Math.max(1, yScale(0) - yScale(Math.abs(countUp)))
  const barX = xScale(datum.x)
  const barY = countUp > 0 ? yScale(0) - barHeight : yScale(0)
  const fill = getColor({ ...datum, y: countUp })

  return (
    <Bar
      x={barX}
      y={barY}
      width={barWidth}
      height={barHeight}
      fill={fill}
      {...restProps}
    />
  )
}

BreakEvenBarChart.propTypes = {
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
    })
  ).isRequired,
  outsideScopeIndex: PropTypes.number,
  breakEvenIndex: PropTypes.number,
  withEllipsis: PropTypes.bool,
  compact: PropTypes.bool,
}

Ellipsis.propTypes = {
  xStart: PropTypes.number.isRequired,
  yStart: PropTypes.number.isRequired,
  fill: PropTypes.string,
}

SlideBar.propTypes = {
  datum: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  getColor: PropTypes.func,
  xScale: PropTypes.any,
  yScale: PropTypes.any,
}

export default BreakEvenBarChart
